import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, XCircle, RefreshCw, Settings, Trash2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';

const GoogleCalendarSettings = () => {
  const [settings, setSettings] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  
  // Form state for settings
  const [formState, setFormState] = useState({
    isEnabled: true,
    syncDirection: 'bidirectional',
    syncActivityTypes: [],
    syncStatusFilter: [],
    syncOnlyAssignedToMe: false
  });

  const activityTypes = ['Call', 'Meeting', 'Email', 'Task', 'Follow-up', 'Demo', 'Proposal', 'Contract Review', 'Negotiation', 'Training', 'Support', 'Other'];
  const statusOptions = ['Planned', 'In Progress', 'Done'];
  const syncDirectionOptions = [
    { value: 'export', label: 'Export Only (CRM → Google)' },
    { value: 'import', label: 'Import Only (Google → CRM)' },
    { value: 'bidirectional', label: 'Bidirectional (Both ways)' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${api.baseURL}/api/google-calendar/settings`, {
        headers: api.getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to load settings');
      
      const data = await response.json();
      
      setConnected(data.connected);
      
      if (data.connected && data.settings) {
        setSettings(data.settings);
        setFormState({
          isEnabled: data.settings.isEnabled,
          syncDirection: data.settings.syncDirection,
          syncActivityTypes: data.settings.syncActivityTypes || [],
          syncStatusFilter: data.settings.syncStatusFilter || [],
          syncOnlyAssignedToMe: data.settings.syncOnlyAssignedToMe
        });
      }
    } catch (error) {
      console.error('Error loading Google Calendar settings:', error);
      toast.error('Failed to load Google Calendar settings');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await fetch(`${api.baseURL}/api/google-calendar/auth/url`, {
        headers: api.getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to get auth URL');
      
      const data = await response.json();
      
      // Open OAuth window
      const width = 600;
      const height = 700;
      const left = (window.screen.width / 2) - (width / 2);
      const top = (window.screen.height / 2) - (height / 2);
      
      const authWindow = window.open(
        data.authUrl,
        'Google Calendar Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Listen for callback
      window.addEventListener('message', async (event) => {
        if (event.data.type === 'google-calendar-auth-success') {
          authWindow?.close();
          toast.success('Google Calendar connected successfully');
          await loadSettings();
        } else if (event.data.type === 'google-calendar-auth-error') {
          authWindow?.close();
          toast.error(event.data.error || 'Failed to connect Google Calendar');
        }
      });
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      toast.error('Failed to initiate Google Calendar connection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Calendar? This will stop all syncing.')) {
      return;
    }
    
    try {
      const response = await fetch(`${api.baseURL}/api/google-calendar/disconnect`, {
        method: 'DELETE',
        headers: api.getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to disconnect');
      
      toast.success('Google Calendar disconnected successfully');
      setConnected(false);
      setSettings(null);
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(`${api.baseURL}/api/google-calendar/settings`, {
        method: 'PUT',
        headers: {
          ...api.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formState)
      });
      
      if (!response.ok) throw new Error('Failed to update settings');
      
      toast.success('Settings updated successfully');
      await loadSettings();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${api.baseURL}/api/google-calendar/sync/now`, {
        method: 'POST',
        headers: api.getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to trigger sync');
      
      const data = await response.json();
      
      toast.success(`Sync completed: ${data.exported} exported, ${data.imported} imported`);
      await loadSettings();
      await loadSyncLogs();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync with Google Calendar');
    } finally {
      setSyncing(false);
    }
  };

  const loadSyncLogs = async () => {
    try {
      const response = await fetch(`${api.baseURL}/api/google-calendar/sync/logs?limit=20`, {
        headers: api.getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to load logs');
      
      const data = await response.json();
      setSyncLogs(data.logs || []);
    } catch (error) {
      console.error('Error loading sync logs:', error);
    }
  };

  const toggleActivityType = (type) => {
    setFormState(prev => ({
      ...prev,
      syncActivityTypes: prev.syncActivityTypes.includes(type)
        ? prev.syncActivityTypes.filter(t => t !== type)
        : [...prev.syncActivityTypes, type]
    }));
  };

  const toggleStatus = (status) => {
    setFormState(prev => ({
      ...prev,
      syncStatusFilter: prev.syncStatusFilter.includes(status)
        ? prev.syncStatusFilter.filter(s => s !== status)
        : [...prev.syncStatusFilter, status]
    }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading Google Calendar settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Google Calendar Sync</h3>
              <p className="text-sm text-gray-600">Sync your CRM activities with Google Calendar</p>
            </div>
          </div>
          
          {connected ? (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Connected</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">Not Connected</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {!connected ? (
          <div className="text-center py-8">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Connect Google Calendar</h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Sync your CRM activities with your Google Calendar to keep everything in one place.
            </p>
            <button
              onClick={handleConnect}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Connect Google Calendar
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Connection Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">Connected Account</p>
                  <p className="text-sm text-blue-700 mt-1">{settings?.googleEmail}</p>
                  {settings?.lastSyncAt && (
                    <p className="text-xs text-blue-600 mt-2">
                      Last sync: {formatDateTime(settings.lastSyncAt)}
                      {settings.lastSyncStatus && (
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                          settings.lastSyncStatus === 'success' ? 'bg-green-100 text-green-700' :
                          settings.lastSyncStatus === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {settings.lastSyncStatus}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleDisconnect}
                  className="text-sm text-red-600 hover:text-red-700 flex items-center"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Disconnect
                </button>
              </div>
            </div>

            {/* Sync Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  Sync Settings
                </h4>
              </div>

              {/* Enable/Disable Sync */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Sync</label>
                  <p className="text-xs text-gray-500">Turn on/off automatic synchronization</p>
                </div>
                <button
                  onClick={() => setFormState(prev => ({ ...prev, isEnabled: !prev.isEnabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formState.isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formState.isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Sync Direction */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Sync Direction
                </label>
                <select
                  value={formState.syncDirection}
                  onChange={(e) => setFormState(prev => ({ ...prev, syncDirection: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {syncDirectionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Activity Types Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Sync Activity Types (leave empty for all)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {activityTypes.map(type => (
                    <label key={type} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formState.syncActivityTypes.includes(type)}
                        onChange={() => toggleActivityType(type)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Sync Status Filter (leave empty for all)
                </label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map(status => (
                    <label key={status} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formState.syncStatusFilter.includes(status)}
                        onChange={() => toggleStatus(status)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Only Assigned to Me */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="syncOnlyAssignedToMe"
                  checked={formState.syncOnlyAssignedToMe}
                  onChange={(e) => setFormState(prev => ({ ...prev, syncOnlyAssignedToMe: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="syncOnlyAssignedToMe" className="text-sm text-gray-700">
                  Only sync activities assigned to me
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3 pt-4">
                <button
                  onClick={handleSaveSettings}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Save Settings
                </button>
                
                <button
                  onClick={handleSyncNow}
                  disabled={syncing || !formState.isEnabled}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Now
                </button>
              </div>
            </div>

            {/* Sync Logs */}
            <div className="border-t border-gray-200 pt-6">
              <button
                onClick={() => {
                  setShowLogs(!showLogs);
                  if (!showLogs && syncLogs.length === 0) {
                    loadSyncLogs();
                  }
                }}
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-900 hover:text-blue-600"
              >
                <span>Sync History</span>
                {showLogs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>

              {showLogs && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {syncLogs.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No sync history yet</p>
                  ) : (
                    syncLogs.map(log => (
                      <div key={log.LogID} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg text-sm">
                        <div className={`mt-0.5 ${
                          log.SyncStatus === 'success' ? 'text-green-600' :
                          log.SyncStatus === 'failed' ? 'text-red-600' :
                          'text-yellow-600'
                        }`}>
                          {log.SyncStatus === 'success' ? <CheckCircle className="h-4 w-4" /> :
                           log.SyncStatus === 'failed' ? <XCircle className="h-4 w-4" /> :
                           <AlertCircle className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">
                              {log.SyncAction} - {log.SyncDirection}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDateTime(log.SyncedAt)}
                            </span>
                          </div>
                          {log.ActivitySubject && (
                            <p className="text-gray-600 truncate">{log.ActivitySubject}</p>
                          )}
                          {log.ErrorMessage && (
                            <p className="text-red-600 text-xs mt-1">{log.ErrorMessage}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarSettings;
